import { Flex, FormLabel, Grid } from "@eworkit/iris";
import { Formik, FormikProps } from "formik";
import React, { memo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useMedia } from "react-use";
import * as Yup from "yup";

import { IEditableSectionRenderProps, useEditableSubmit } from "@/components/EditableSection";
import { FormikError, FormikTextInput, VALIDATION_TYPES } from "@/components/Formik";
import { MEDIA_SIZE_SM } from "@/core/media";
import { ICodelist } from "@/data/codelists";
import { ICompany, ICompanyContact } from "@/data/companies";
import { TPhoneCodeCodelistParameter } from "@/data/phones";
import { Group, ReactSelectComponents } from "@/eworkitComponents";
import { FormikForm } from "@/formikComponents";
import { ICodelistGenericSelectParams } from "@/views/components/Codelists";
import { FormikCodelistGenericSelect } from "@/views/components/Formik";

import { ICompanyFormikProps } from "../../CompanyData/types";

interface IContactSectionProps extends IEditableSectionRenderProps {
  source: ICompany;
  form: FormikProps<ICompanyContact>;
}

const contactSchema = Yup.object().shape({
  supplierEmail: Yup.string().email(VALIDATION_TYPES.EMAIL).optional(),
  supplierContactPhone: Yup.object()
    .shape(
      {
        phoneNumber: Yup.string().when("phoneCode", {
          is: (val: any) => !!val,
          then: Yup.string()
            .required(VALIDATION_TYPES.REQUIRED)
            .matches(/^\d+$/, VALIDATION_TYPES.NUMBERS)
            .test({
              message: VALIDATION_TYPES.NUMBERS,
              test: (phone: any) => !isNaN(phone),
            }),
          otherwise: Yup.string(),
        }),
        phoneCode: Yup.string()
          .nullable(true)
          .when("phoneNumber", {
            is: (val: any) => !!val,
            then: Yup.string().required(VALIDATION_TYPES.REQUIRED),
            otherwise: Yup.string().nullable(true),
          }),
      },
      [
        ["phoneCode", "phoneNumber"],
        ["phoneNumber", "phoneCode"],
      ],
    )
    .nullable(),
  financeEmail: Yup.string().email(VALIDATION_TYPES.EMAIL).required(VALIDATION_TYPES.REQUIRED),
  financeContactPhone: Yup.object().shape({
    phoneNumber: Yup.string().matches(/^\d+$/, VALIDATION_TYPES.NUMBERS).required(VALIDATION_TYPES.REQUIRED),
    phoneCode: Yup.string().required(VALIDATION_TYPES.REQUIRED),
  }),
});

const PHONE_CODES_CODELIST_QUERY: ICodelistGenericSelectParams = { codelistName: "phone-codes" };

export const SupplierContactSectionForm: React.FC<IContactSectionProps> = memo(({ editableProps, form }) => {
  const { t } = useTranslation("session");
  const isSmallScreen = useMedia(MEDIA_SIZE_SM);

  useEditableSubmit(form, editableProps);

  const setParameterAsValue = useCallback(
    (option: Array<ICodelist<TPhoneCodeCodelistParameter>>) =>
      option.map(o => ({
        ...o,
        value: o.parameter.format,
      })),
    [],
  );

  const CodeListComponent = {
    SingleValue: (props: any) => (
      <ReactSelectComponents.SingleValue {...props}>{props.data.parameter.format}</ReactSelectComponents.SingleValue>
    ),
  };

  return (
    <FormikForm>
      <Flex flexDirection="column" gap="sm">
        <Grid columns={isSmallScreen ? "auto" : "50%"} gap={["xs", null, "xs"]}>
          <FormLabel label={t("USERS.SETTINGS.COMPANY_CONTACT_DETAILS.FORM.CONTACT.SUPPLIER_PHONE")} />

          <Flex gap="xs" flexDirection="row" alignItems="center">
            <Group width={6}>
              <FormikCodelistGenericSelect
                size="md"
                simpleValue
                className="phone-code-select"
                components={CodeListComponent}
                query={PHONE_CODES_CODELIST_QUERY}
                name="supplierContactPhone.phoneCode"
                filterAfterFetch={setParameterAsValue}
                value={form.values.supplierContactPhone?.phoneCode}
              />
            </Group>

            <FormikTextInput size="md" name="supplierContactPhone.phoneNumber" />
          </Flex>

          <FormikError name="supplierContactPhone.phoneCode" data-test="error-supplierContactPhone-phoneCode" />
          <FormikError name="supplierContactPhone.phoneNumber" data-test="error-supplierContactPhone-phoneNumber" />
        </Grid>

        <Grid columns={isSmallScreen ? "auto" : "50%"} gap={["xs", null, "xs"]}>
          <FormLabel label={t("USERS.SETTINGS.COMPANY_CONTACT_DETAILS.FORM.CONTACT.SUPPLIER_EMAIL")} />

          <FormikTextInput type="text" name="supplierEmail" />
          <FormikError name="supplierEmail" data-test="error-supplierEmail" />
        </Grid>

        <Grid columns={isSmallScreen ? "auto" : "50%"} gap={["xs", null, "xs"]}>
          <FormLabel label={t("USERS.SETTINGS.COMPANY_CONTACT_DETAILS.FORM.CONTACT.FINANCE_PHONE")} required />

          <Flex gap="xs" flexDirection="row" alignItems="center">
            <Group width={6}>
              <FormikCodelistGenericSelect
                required
                size="md"
                simpleValue
                isClearable={false}
                className="phone-code-select"
                components={CodeListComponent}
                query={PHONE_CODES_CODELIST_QUERY}
                name="financeContactPhone.phoneCode"
                filterAfterFetch={setParameterAsValue}
                value={form.values.financeContactPhone.phoneCode}
              />
            </Group>

            <FormikTextInput
              clearable={false}
              required
              size="md"
              className="phone-number-input"
              name="financeContactPhone.phoneNumber"
            />
          </Flex>

          <FormikError name="financeContactPhone.phoneNumber" data-test="error-financeContactPhone-phoneNumber" />
        </Grid>

        <Grid columns={isSmallScreen ? "auto" : "50%"} gap={["xs", null, "xs"]}>
          <FormLabel label={t("USERS.SETTINGS.COMPANY_CONTACT_DETAILS.FORM.CONTACT.FINANCE_EMAIL")} required />

          <FormikTextInput clearable={false} type="text" name="financeEmail" required />
          <FormikError name="financeEmail" data-test="error-financeEmail" />
        </Grid>
      </Flex>
    </FormikForm>
  );
});

interface IContactFormikProps
  extends Omit<ICompanyFormikProps<ICompanyContact>, "validationSchema">,
    Omit<IContactSectionProps, "form"> {}

export const SupplierContactEdit = ({ source, onSubmit, initialValues, ...rest }: IContactFormikProps) => (
  <Formik enableReinitialize onSubmit={onSubmit} validationSchema={contactSchema} initialValues={{ ...initialValues }}>
    {(form: FormikProps<ICompanyContact>) => <SupplierContactSectionForm {...rest} source={source} form={form} />}
  </Formik>
);
